import React, { useState, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Switch,
    FormControlLabel,
    Slider,
    Card,
    CardContent,
    CardActionArea,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
    Skeleton,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    DeleteForever as DeletePhotoIcon,
    PhotoCamera as PhotoIcon,
    MoreVert as MoreVertIcon,
    DirectionsCar as CarIcon,
    EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { PageHeader, ViewToggle } from '../../components/admin';
import type { ViewMode } from '../../components/admin';
import type { Technika } from '../../api/JsdhNotifikaceService/generated/models/Technika';
import type { TechnikaRequest } from '../../api/JsdhNotifikaceService/generated/models/TechnikaRequest';
import {
    useAllTechnikaQuery,
    useCreateTechnikaMutation,
    useUpdateTechnikaMutation,
    useDeleteTechnikaMutation,
    useUploadFotografieMutation,
    useDeleteFotografieMutation,
    useTechnikaDostupnostPrehledQuery,
    useOdhlasitTechnikuMutation,
    useUkoncitDostupnostMutation,
} from '../../api/JsdhNotifikaceService/service/TechnikaMutations';
import { axiosInstance } from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { TECHNIKA_FOTOGRAFIE_URL } from '../../api/JsdhNotifikaceService/url';
import { useAuth } from '../../context/AuthContext';
import DostupnostTechnikaDialog from '../../components/admin/DostupnostTechnikaDialog';
import TechnikaParovaniInfo from '../../components/admin/TechnikaParovaniInfo';
import { DUVOD_LABELS } from '../../api/JsdhNotifikaceService/generated/models/DostupnostTechnika';
import type { TechnikaDostupnostPrehled } from '../../api/JsdhNotifikaceService/generated/models/DostupnostTechnika';
import ObsazeniNahledPanel from './components/ObsazeniNahledPanel';
import FeatureGate from '../../components/FeatureGate';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import NotFoundPage from '../NotFoundPage';

const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const StkChip: React.FC<{ status?: Technika['stkStatus']; expirace?: string }> = ({ status, expirace }) => {
    if (status === 'PLATNA' && expirace) {
        return <Chip label={`STK: Platná do ${formatDate(expirace)}`} color="success" size="small" />;
    }
    if (status === 'BLIZI_SE_EXPIRACE' && expirace) {
        return <Chip label={`STK: Blíží se — do ${formatDate(expirace)}`} color="warning" size="small" />;
    }
    if (status === 'EXPIROVANA' && expirace) {
        return <Chip label={`STK: Expirovaná ${formatDate(expirace)}`} color="error" size="small" />;
    }
    return <Chip label="STK: Chybí" variant="outlined" size="small" />;
};

const StavChip: React.FC<{ aktivni: boolean }> = ({ aktivni }) => (
    <Chip
        label={aktivni ? 'Aktivní' : 'Neaktivní'}
        color={aktivni ? 'success' : 'default'}
        size="small"
    />
);

/** Hook to load a photo blob URL with React Query caching + blob cleanup. */
const useTechnikaPhoto = (id: number, maFotografii: boolean) => {
    const { data: src, isLoading: loading } = useQuery<string | null>({
        queryKey: ['technika-photo', id],
        queryFn: async () => {
            try {
                const res = await axiosInstance.get(TECHNIKA_FOTOGRAFIE_URL(id), { responseType: 'blob' });
                return URL.createObjectURL(res.data);
            } catch {
                return null;
            }
        },
        enabled: maFotografii,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    return { src: src ?? null, loading };
};

const TechnikaCard: React.FC<{
    t: Technika;
    onEdit: (t: Technika) => void;
    onDelete: (t: Technika) => void;
    onNavigate: (id: number) => void;
    dostupnost?: TechnikaDostupnostPrehled;
    onDostupnost: (p: TechnikaDostupnostPrehled) => void;
}> = ({ t, onEdit, onDelete, onNavigate, dostupnost, onDostupnost }) => {
    const { src: photoSrc, loading: photoLoading } = useTechnikaPhoto(t.id, t.maFotografii);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    return (
        <Card variant="outlined" sx={{ position: 'relative' }}>
            <CardActionArea
                onClick={() => onNavigate(t.id)}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                }}
            >
                {/* Photo area — side on desktop, top on mobile */}
                <Box
                    sx={{
                        width: { xs: '100%', sm: 200 },
                        minWidth: { sm: 200 },
                        height: { xs: 160, sm: 'auto' },
                        minHeight: { sm: 140 },
                        flexShrink: 0,
                        overflow: 'hidden',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {t.maFotografii ? (
                        photoLoading ? (
                            <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
                        ) : photoSrc ? (
                            <Box
                                component="img"
                                src={photoSrc}
                                alt={t.nazev}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        ) : (
                            <CarIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                        )
                    ) : (
                        <CarIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                    )}
                </Box>

                {/* Content */}
                <CardContent sx={{ flex: 1, py: 2 }}>
                    <Typography variant="h6" fontWeight={700} noWrap>
                        {t.volaciZnak}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {t.nazev}
                    </Typography>
                    {t.spz && (
                        <Typography variant="body2" color="text.secondary">
                            SPZ: {t.spz}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <StkChip status={t.stkStatus} expirace={t.stkExpirace} />
                        <StavChip aktivni={t.aktivni} />
                        {dostupnost?.dostupnost && (
                            <Chip
                                label={`NEDOSTUPNÁ — ${DUVOD_LABELS[dostupnost.dostupnost.duvod || 'JINE']}`}
                                color="warning"
                                size="small"
                            />
                        )}
                    </Box>
                </CardContent>
            </CardActionArea>

            {/* Menu button */}
            <IconButton
                size="small"
                aria-label="Akce vozidla"
                onClick={handleMenuOpen}
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' } }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                onClick={handleMenuClose}
            >
                <MenuItem onClick={() => onEdit(t)}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Upravit</ListItemText>
                </MenuItem>
                {/* Odhlašování z provozu je modul Dostupnost techniky — v mobilu ho
                    hradí tentýž klíč. Chip NEDOSTUPNÁ výš se schválně NEskrývá:
                    stav vozidla je informace k výjezdu a schovat ji by vozidlo
                    ukázalo jako pojízdné, i když není. */}
                <FeatureGate flag="MODUL_DOSTUPNOST_TECHNIKY">
                    <MenuItem onClick={() => dostupnost && onDostupnost(dostupnost)} disabled={!dostupnost}>
                        <ListItemIcon><EventBusyIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>
                            {dostupnost?.dostupnost ? 'Přihlásit zpět' : 'Odhlásit z provozu'}
                        </ListItemText>
                    </MenuItem>
                </FeatureGate>
                <MenuItem onClick={() => onDelete(t)}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Smazat</ListItemText>
                </MenuItem>
            </Menu>
        </Card>
    );
};

const TechnikaPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isAdmin } = useAuth();
    // Modul Dostupnost techniky (odhlašování vozidel z provozu) je stejná funkce,
    // jakou v mobilu hradí `MODUL_DOSTUPNOST_TECHNIKY` — proto tentýž klíč.
    // Správa techniky (CRUD, kontroly, STK) modul NENÍ a flagem se nehradí.
    const { enabled: modulDostupnostTechniky } = useFeatureFlag('MODUL_DOSTUPNOST_TECHNIKY');
    const { data: technika, isLoading, error } = useAllTechnikaQuery({ enabled: isAdmin });
    const { data: dostupnostPrehled, isLoading: prehledLoading } = useTechnikaDostupnostPrehledQuery();
    const odhlasitMutation = useOdhlasitTechnikuMutation();
    const ukoncitMutation = useUkoncitDostupnostMutation();
    const [dostupnostDialog, setDostupnostDialog] = useState<TechnikaDostupnostPrehled | null>(null);
    const createMutation = useCreateTechnikaMutation();
    const updateMutation = useUpdateTechnikaMutation();
    const deleteMutation = useDeleteTechnikaMutation();
    const uploadFotoMutation = useUploadFotografieMutation();
    const deleteFotoMutation = useDeleteFotografieMutation();

    const [viewMode, setViewMode] = useState<ViewMode>('cards');

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedTechnika, setSelectedTechnika] = useState<Technika | null>(null);
    const [formData, setFormData] = useState<TechnikaRequest>({
        volaciZnak: '',
        nazev: '',
        spz: '',
        aktivni: true,
        vyzadujeStrojnika: false,
        vyzadujeVelitele: false,
    });

    // Photo upload
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [fotoPreview, setFotoPreview] = useState<string | null>(null);

    // Crop dialog
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    const handleOpenCreate = () => {
        setSelectedTechnika(null);
        setFormData({ volaciZnak: '', nazev: '', spz: '', aktivni: true, vyzadujeStrojnika: false, vyzadujeVelitele: false });
        setFotoPreview(null);
        setOpenDialog(true);
    };

    const handleOpenEdit = (t: Technika) => {
        setSelectedTechnika(t);
        setFormData({
            volaciZnak: t.volaciZnak,
            nazev: t.nazev,
            spz: t.spz ?? '',
            aktivni: t.aktivni,
            maxOsob: t.maxOsob,
            minOsob: t.minOsob,
            vyzadujeStrojnika: t.vyzadujeStrojnika ?? false,
            vyzadujeVelitele: t.vyzadujeVelitele ?? false,
            poradiPlneni: t.poradiPlneni,
        });
        if (t.maFotografii) {
            axiosInstance.get(TECHNIKA_FOTOGRAFIE_URL(t.id), { responseType: 'blob' })
                .then(res => setFotoPreview(URL.createObjectURL(res.data)))
                .catch(() => setFotoPreview(null));
        } else {
            setFotoPreview(null);
        }
        setOpenDialog(true);
    };

    const handleOpenDelete = (t: Technika) => {
        setSelectedTechnika(t);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedTechnika(null);
        setFormData({ volaciZnak: '', nazev: '', spz: '', aktivni: true, vyzadujeStrojnika: false, vyzadujeVelitele: false });
        if (fotoPreview) URL.revokeObjectURL(fotoPreview);
        setFotoPreview(null);
    };

    const handleSubmit = () => {
        if (selectedTechnika) {
            updateMutation.mutate(
                { id: selectedTechnika.id, data: formData },
                { onSuccess: () => handleCloseDialog() }
            );
        } else {
            createMutation.mutate(formData, {
                onSuccess: (created) => {
                    // Po vytvoreni otevrit editacni dialog pro upload fotografie
                    setSelectedTechnika(created);
                    setFotoPreview(null);
                },
            });
        }
    };

    const handleDelete = () => {
        if (selectedTechnika) {
            deleteMutation.mutate(selectedTechnika.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelectedTechnika(null);
                },
            });
        }
    };

    const handleFotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && selectedTechnika) {
            if (file.size > 10 * 1024 * 1024) return;
            const reader = new FileReader();
            reader.onload = () => {
                setCropImage(reader.result as string);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setCropDialogOpen(true);
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<File> => {
        const image = await new Promise<HTMLImageElement>((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = imageSrc;
        });

        const canvas = document.createElement('canvas');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(
            image,
            pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
            0, 0, pixelCrop.width, pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(new File([blob!], 'fotografie.jpg', { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.9);
        });
    };

    const handleCropConfirm = async () => {
        if (!cropImage || !croppedAreaPixels || !selectedTechnika) return;
        const croppedFile = await getCroppedImg(cropImage, croppedAreaPixels);
        const previewUrl = URL.createObjectURL(croppedFile);
        uploadFotoMutation.mutate(
            { id: selectedTechnika.id, file: croppedFile },
            {
                onSuccess: () => {
                    setFotoPreview(previewUrl);
                    setCropDialogOpen(false);
                    setCropImage(null);
                },
            }
        );
    };

    const handleFotoDelete = () => {
        if (selectedTechnika) {
            deleteFotoMutation.mutate(selectedTechnika.id, {
                onSuccess: () => {
                    setFotoPreview(null);
                },
            });
        }
    };

    const dostupnostByTechnikaId = useMemo(() => {
        const map = new Map<number, TechnikaDostupnostPrehled>();
        (dostupnostPrehled || []).forEach(p => map.set(p.technikaId, p));
        return map;
    }, [dostupnostPrehled]);

    const handleDostupnostAction = (p: TechnikaDostupnostPrehled) => {
        if (p.dostupnost) {
            ukoncitMutation.mutate(p.dostupnost.id);
        } else {
            setDostupnostDialog(p);
        }
    };

    if (!isAdmin) {
        // Členovi je na téhle routě dostupný JEN modul Dostupnost techniky — když je
        // vypnutý, nemá tu co vidět. Stejné 404 jako u rout s `requireFeature`,
        // aby se skryté funkce nemapovaly. Gating je až tady, ne na routě: tutéž
        // routu používá admin pro správu techniky, která modul není.
        if (!modulDostupnostTechniky) {
            return <NotFoundPage />;
        }
        return (
            <Box>
                <PageHeader title="Technika — dostupnost" />
                {prehledLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2 }}>
                        {(dostupnostPrehled || []).map((p) => (
                            <Card key={p.technikaId} variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" fontWeight={700}>{p.volaciZnak}</Typography>
                                    <Typography variant="body2" color="text.secondary">{p.nazev}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {p.dostupnost ? (
                                            <>
                                                <Chip
                                                    label={`NEDOSTUPNÁ — ${DUVOD_LABELS[p.dostupnost.duvod || 'JINE']}`}
                                                    color="warning"
                                                    size="small"
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {p.dostupnost.platnostDo
                                                        ? `do ${new Date(p.dostupnost.platnostDo).toLocaleString('cs-CZ')}`
                                                        : 'do odvolání'}
                                                    {p.dostupnost.vytvorilJmeno && ` · odhlásil ${p.dostupnost.vytvorilJmeno}`}
                                                </Typography>
                                            </>
                                        ) : (
                                            <Chip label="Dostupná" color="success" size="small" />
                                        )}
                                    </Box>
                                    <Box sx={{ mt: 1.5 }}>
                                        {p.dostupnost ? (
                                            <Button size="small" variant="outlined" color="success"
                                                onClick={() => ukoncitMutation.mutate(p.dostupnost!.id)}
                                                disabled={ukoncitMutation.isPending}>
                                                Přihlásit zpět
                                            </Button>
                                        ) : (
                                            <Button size="small" variant="outlined" color="warning"
                                                onClick={() => setDostupnostDialog(p)}>
                                                Odhlásit z provozu
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}
                <DostupnostTechnikaDialog
                    open={Boolean(dostupnostDialog)}
                    onClose={() => setDostupnostDialog(null)}
                    vozidloLabel={dostupnostDialog ? `${dostupnostDialog.nazev} (${dostupnostDialog.volaciZnak})` : ''}
                    onSubmit={(data) => {
                        if (dostupnostDialog) {
                            odhlasitMutation.mutate(
                                { id: dostupnostDialog.technikaId, data },
                                { onSuccess: () => setDostupnostDialog(null) }
                            );
                        }
                    }}
                    isPending={odhlasitMutation.isPending}
                />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">Chyba při načítání techniky: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Technika"
                action={
                    <>
                        <ViewToggle value={viewMode} onChange={setViewMode} />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                        >
                            Přidat techniku
                        </Button>
                    </>
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !technika || technika.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <CarIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Žádná technika
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Zatím nebyla přidána žádná technika. Začněte tlačítkem výše.
                    </Typography>
                </Paper>
            ) : viewMode === 'cards' ? (
                /* Cards view */
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                        gap: 2,
                    }}
                >
                    {technika.map((t) => (
                        <TechnikaCard
                            key={t.id}
                            t={t}
                            onEdit={handleOpenEdit}
                            onDelete={handleOpenDelete}
                            onNavigate={(id) => navigate(`/admin/technika/${id}`)}
                            dostupnost={dostupnostByTechnikaId.get(t.id)}
                            onDostupnost={handleDostupnostAction}
                        />
                    ))}
                </Box>
            ) : (
                /* Table view */
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Volací znak</TableCell>
                                <TableCell>Název</TableCell>
                                <TableCell>SPZ</TableCell>
                                <TableCell>STK</TableCell>
                                <TableCell>Stav</TableCell>
                                <TableCell>Dostupnost</TableCell>
                                <TableCell align="center">Foto</TableCell>
                                <TableCell>Vytvořeno</TableCell>
                                <TableCell align="right">Akce</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {technika.map((t) => (
                                <TableRow
                                    key={t.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/admin/technika/${t.id}`)}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            {t.volaciZnak}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{t.nazev}</TableCell>
                                    <TableCell>{t.spz}</TableCell>
                                    <TableCell>
                                        {t.stkExpirace ? formatDate(t.stkExpirace) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <StavChip aktivni={t.aktivni} />
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const d = dostupnostByTechnikaId.get(t.id);
                                            return d?.dostupnost ? (
                                                <Chip
                                                    label={`NEDOSTUPNÁ — ${DUVOD_LABELS[d.dostupnost.duvod || 'JINE']}`}
                                                    color="warning"
                                                    size="small"
                                                />
                                            ) : null;
                                        })()}
                                    </TableCell>
                                    <TableCell align="center">
                                        {t.maFotografii ? <PhotoIcon fontSize="small" color="primary" /> : null}
                                    </TableCell>
                                    <TableCell>
                                        {t.createdAt ? new Date(t.createdAt).toLocaleString('cs-CZ') : ''}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="Upravit">
                                            <IconButton size="small" onClick={() => handleOpenEdit(t)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Smazat">
                                            <IconButton size="small" color="error" onClick={() => handleOpenDelete(t)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <FeatureGate flag="FEATURE_OBSAZOVANI_VOZIDEL">
                <ObsazeniNahledPanel vozidla={technika ?? []} />
            </FeatureGate>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedTechnika ? 'Upravit techniku' : 'Nová technika'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TechnikaParovaniInfo />
                        <TextField
                            label="Volací znak"
                            value={formData.volaciZnak}
                            onChange={(e) => setFormData({ ...formData, volaciZnak: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 50 }}
                            helperText="Podle volacího znaku se vozidlo primárně páruje s poplachem (např. HPE 542)"
                        />
                        <TextField
                            label="Název"
                            value={formData.nazev}
                            onChange={(e) => setFormData({ ...formData, nazev: e.target.value })}
                            required
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                            helperText="Zapište přesně tak, jak název posílá KOPIS (např. CAS 32/8200/800-S3R T815)"
                        />
                        <TextField
                            label="SPZ"
                            value={formData.spz || ''}
                            onChange={(e) => setFormData({ ...formData, spz: e.target.value })}
                            fullWidth
                            inputProps={{ maxLength: 20 }}
                            helperText="Registrační značka (volitelné)"
                        />
                        <TextField
                            label="Počet míst (max)"
                            type="number"
                            value={formData.maxOsob ?? ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    maxOsob: e.target.value === '' ? undefined : Number(e.target.value),
                                })
                            }
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                        <TextField
                            label="Minimální počet (vč. strojníka a velitele)"
                            type="number"
                            value={formData.minOsob ?? ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    minOsob: e.target.value === '' ? undefined : Number(e.target.value),
                                })
                            }
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                        <TextField
                            label="Pořadí plnění (1 = prvovýjezd)"
                            type="number"
                            value={formData.poradiPlneni ?? ''}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    poradiPlneni: e.target.value === '' ? undefined : Number(e.target.value),
                                })
                            }
                            fullWidth
                            inputProps={{ min: 1 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.vyzadujeStrojnika ?? false}
                                    onChange={(e) => setFormData({ ...formData, vyzadujeStrojnika: e.target.checked })}
                                />
                            }
                            label="Vyžaduje strojníka (řidiče)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.vyzadujeVelitele ?? false}
                                    onChange={(e) => setFormData({ ...formData, vyzadujeVelitele: e.target.checked })}
                                />
                            }
                            label="Vyžaduje velitele"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aktivni ?? true}
                                    onChange={(e) => setFormData({ ...formData, aktivni: e.target.checked })}
                                />
                            }
                            label="Aktivní"
                        />

                        {/* Photo upload - only for existing technika */}
                        {selectedTechnika && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Fotografie
                                </Typography>
                                {fotoPreview ? (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box
                                            component="img"
                                            src={fotoPreview}
                                            alt="Fotografie techniky"
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: 200,
                                                objectFit: 'contain',
                                                borderRadius: 1,
                                                display: 'block',
                                            }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Button
                                                size="small"
                                                startIcon={<CloudUploadIcon />}
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                Nahrát novou
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeletePhotoIcon />}
                                                onClick={handleFotoDelete}
                                                disabled={deleteFotoMutation.isPending}
                                            >
                                                Smazat
                                            </Button>
                                            {(uploadFotoMutation.isPending || deleteFotoMutation.isPending) && (
                                                <CircularProgress size={20} sx={{ ml: 1, alignSelf: 'center' }} />
                                            )}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            onClick={() => fileInputRef.current?.click()}
                                            sx={{
                                                width: 120,
                                                height: 90,
                                                border: '2px dashed',
                                                borderColor: 'grey.400',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                bgcolor: 'grey.50',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'grey.100',
                                                },
                                            }}
                                        >
                                            <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                        </Box>
                                        {uploadFotoMutation.isPending && <CircularProgress size={24} />}
                                    </Box>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                                    onChange={handleFotoUpload}
                                    style={{ display: 'none' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    PNG, JPG, JPEG nebo SVG, max 10 MB (bude oříznut na 4:3)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            !formData.volaciZnak.trim() ||
                            !formData.nazev.trim() ||
                            createMutation.isPending ||
                            updateMutation.isPending
                        }
                    >
                        {(createMutation.isPending || updateMutation.isPending) && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        {selectedTechnika ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Smazat techniku?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat techniku{' '}
                        <strong>{selectedTechnika?.nazev} ({selectedTechnika?.volaciZnak})</strong>?
                        Tato akce odstraní i vazby na existující poplachy.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending && (
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                        )}
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop Dialog */}
            <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Oříznout fotografii</DialogTitle>
                <DialogContent sx={{ position: 'relative', height: 400, p: 0 }}>
                    {cropImage && (
                        <Cropper
                            image={cropImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={4 / 3}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ flex: 1, px: 2 }}>
                        <Slider value={zoom} min={1} max={3} step={0.1} onChange={(_, v) => setZoom(v as number)} />
                    </Box>
                    <Button onClick={() => setCropDialogOpen(false)}>Zrušit</Button>
                    <Button variant="contained" onClick={handleCropConfirm} disabled={uploadFotoMutation.isPending}>
                        Oříznout a nahrát
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dostupnost Dialog */}
            <DostupnostTechnikaDialog
                open={Boolean(dostupnostDialog)}
                onClose={() => setDostupnostDialog(null)}
                vozidloLabel={dostupnostDialog ? `${dostupnostDialog.nazev} (${dostupnostDialog.volaciZnak})` : ''}
                onSubmit={(data) => {
                    if (dostupnostDialog) {
                        odhlasitMutation.mutate(
                            { id: dostupnostDialog.technikaId, data },
                            { onSuccess: () => setDostupnostDialog(null) }
                        );
                    }
                }}
                isPending={odhlasitMutation.isPending}
            />
        </Box>
    );
};

export default TechnikaPage;
